import { useAuthStore } from '@/stores/auth-store';
import { bangkokClockTime } from '@/lib/datetime';
import { useMinuteTick } from '@/hooks/useMinuteTick';
import { LAYER } from '@/components/ui/layers';

/**
 * Enough tiles to cover a rotated plane larger than any desktop viewport, so a
 * crop of any corner of a screenshot still carries a whole line.
 */
const TILES = 120;

interface Props {
  /**
   * The clock, injected. Left out in the app, where the component ticks itself
   * once a minute; a test passes a fixed instant rather than racing a timer
   * (§6.8).
   */
  now?: Date;
}

/**
 * The identity stamp every back-office page carries: who is looking, at what
 * rank, from which address, at what time on the platform's clock.
 *
 * It exists to make a leaked screenshot name its source, which is why it sits
 * ABOVE the dialogs (a screenshot of an open approval dialog has to carry it
 * too) and why it is drawn out of flow: `fixed` + `overflow-hidden` so it never
 * moves the shell or adds a second scrollbar, `pointer-events-none` so it never
 * eats a click, `aria-hidden` so a screen reader does not read the same line a
 * hundred and twenty times.
 */
export function Watermark({ now }: Props) {
  const user = useAuthStore((s) => s.user);
  const tick = useMinuteTick(now);

  if (!user) return null;

  const role = user.roleCode ?? (user.roleLevel != null ? `L${user.roleLevel}` : '-');
  const line = [user.username, role, user.id, user.ip ?? '-', bangkokClockTime(tick)].join(
    ' | ',
  );

  return (
    <div
      data-testid="watermark"
      aria-hidden="true"
      className={`pointer-events-none fixed inset-0 select-none overflow-hidden ${LAYER.watermark}`}
    >
      <div className="absolute left-1/2 top-1/2 flex h-[220vh] w-[260vw] -translate-x-1/2 -translate-y-1/2 -rotate-[24deg] flex-wrap content-start gap-x-16 gap-y-20">
        {Array.from({ length: TILES }, (_, i) => (
          <span
            key={i}
            className="whitespace-nowrap font-mono text-xs text-foreground opacity-[0.07]"
          >
            {line}
          </span>
        ))}
      </div>
    </div>
  );
}
